export const SITE = {
  name: 'Cosmic Secrets',
  url: 'https://cosmic-secrets.com',
  domain: 'cosmic-secrets.com',
  email: 'hello@cosmic-secrets.com',
  tagline: 'Tarot, zodiac, and numerology guides for curious readers.',
  description:
    'Cosmic Secrets publishes polished, long-form guides on tarot, zodiac, Chinese zodiac, and numerology with practical explanations, deep symbolism, and clear internal navigation.',
  updated: '2026-05-04',
  founded: '2024-01-01',
  publisher: 'Cosmic Secrets Editorial',
  themeColor: '#08131f',
  defaultImage: 'https://cosmic-secrets.com/cosmic-secrets-icon.png',
};

const CLOUDFRONT_BASE = 'https://d3zlw6prgce9n.cloudfront.net';

const zodiacImageMap = {
  aries: '/aries1.png',
  taurus: '/taurus1.png',
  gemini: '/gemini1.png',
  cancer: '/cancer1.png',
  leo: '/leo1.png',
  virgo: '/virgo1.png',
  libra: '/libra2.png',
  scorpio: '/scorpio1.png',
  sagittarius: '/sagittarius1.png',
  capricorn: '/capricorn1.png',
  aquarius: '/aquarius1.png',
  pisces: '/pisces1.png',
};

const lifePathImageMap = {
  1: '/LifePath1.png',
  2: '/LifePath2.png',
  3: '/LifePath3.png',
  4: '/LifePath4.png',
  5: '/LifePath5.png',
  6: '/LifePath6.png',
  7: '/LifePath7.png',
  8: '/LifePath8.png',
  9: '/LifePath9.png',
};

const chaldeanImageMap = {
  1: '/Sun1.png',
  2: '/Moon1.png',
  3: '/Jupiter1.png',
  4: '/Moon2.png',
  5: '/Mercury1.png',
  6: '/Venus.png',
  7: '/Moon3.png',
  8: '/Saturn1.png',
  9: '/Mars.png',
};

const chineseZodiacImageMap = {
  Rat: '/ChineseZodiacRat.png',
  Ox: '/ChineseZodiacOx.png',
  Tiger: '/ChineseZodiacTiger.png',
  Rabbit: '/ChineseZodiacRabbit.png',
  Dragon: '/ChineseZodiacDragon.png',
  Snake: '/ChineseZodiacSnake.png',
  Horse: '/ChineseZodiacHorse.png',
  Goat: '/ChineseZodiacGoat.png',
  Monkey: '/ChineseZodiacMonkey.png',
  Rooster: '/ChineseZodiacRooster.png',
  Dog: '/ChineseZodiacDog.png',
  Pig: '/ChineseZodiacPig.png',
};

const majorArcanaImageMap = {
  fool_tarot: '/Fool.jpg',
  magician_tarot: `${CLOUDFRONT_BASE}/01_Magician.jpg`,
  high_priestess_tarot: `${CLOUDFRONT_BASE}/02_High_Priestess.jpg`,
  empress_tarot: `${CLOUDFRONT_BASE}/03_Empress.jpg`,
  emperor_tarot: `${CLOUDFRONT_BASE}/04_Emperor.jpg`,
  hierophant_tarot: `${CLOUDFRONT_BASE}/05_Hierophant.jpg`,
  lovers_tarot: `${CLOUDFRONT_BASE}/06_Lovers.jpg`,
  chariot_tarot: `${CLOUDFRONT_BASE}/07_Chariot.jpg`,
  strength_tarot: `${CLOUDFRONT_BASE}/08_Strength.jpg`,
  hermit_tarot: `${CLOUDFRONT_BASE}/09_Hermit.jpg`,
  wheel_of_fortune_tarot: `${CLOUDFRONT_BASE}/10_Wheel_of_Fortune.jpg`,
  justice_tarot: `${CLOUDFRONT_BASE}/11_Justice.jpg`,
  hanged_man_tarot: `${CLOUDFRONT_BASE}/12_Hanged_Man.jpg`,
  death_tarot: `${CLOUDFRONT_BASE}/13_Death.jpg`,
  temperance_tarot: `${CLOUDFRONT_BASE}/14_Temperance.jpg`,
  devil_tarot: `${CLOUDFRONT_BASE}/15_Devil.jpg`,
  tower_tarot: `${CLOUDFRONT_BASE}/16_Tower.jpg`,
  star_tarot: `${CLOUDFRONT_BASE}/17_Star.jpg`,
  moon_tarot: `${CLOUDFRONT_BASE}/18_Moon.jpg`,
  sun_tarot: `${CLOUDFRONT_BASE}/19_Sun.jpg`,
  judgement_tarot: `${CLOUDFRONT_BASE}/20_Judgement.jpg`,
  world_tarot: `${CLOUDFRONT_BASE}/21_World.jpg`,
};

const suitImagePrefixMap = {
  cups: 'Cups',
  wands: 'Wands',
  swords: 'Swords',
  pentacles: 'Pents',
};

const rankImageIndexMap = {
  ace: '01',
  two: '02',
  three: '03',
  four: '04',
  five: '05',
  six: '06',
  seven: '07',
  eight: '08',
  nine: '09',
  ten: '10',
  page: '11',
  knight: '12',
  queen: '13',
  king: '14',
};

function minorArcanaImage(suitSlug, rankSlug) {
  return `${CLOUDFRONT_BASE}/${suitImagePrefixMap[suitSlug]}${rankImageIndexMap[rankSlug]}.jpg`;
}

const zodiacSigns = [
  {
    slug: 'aries',
    name: 'Aries',
    symbol: 'The Ram',
    dates: 'March 21 - April 19',
    element: 'Fire',
    modality: 'Cardinal',
    ruler: 'Mars',
    icon: '♈',
    essence:
      'Aries moves first. It is the zodiac sign of ignition, courage, appetite, and the willingness to begin before every answer is known.',
    strengths: 'decisive, brave, candid, energetic, competitive',
    tension: 'impatient, reactive, forceful, easily bored',
    relationships:
      'Aries needs aliveness in relationship. Directness, momentum, and mutual admiration matter more than polished etiquette.',
    work:
      'At work, Aries thrives anywhere speed, ownership, and initiative are rewarded. Stagnation drains its confidence faster than hard effort.',
    growth:
      'The deep lesson for Aries is learning that leadership is not only about starting the fire. It is also about pacing the flame so it can last.',
    compatibility: 'Leo, Sagittarius, Gemini, and Aquarius often keep Aries inspired.',
  },
  {
    slug: 'taurus',
    name: 'Taurus',
    symbol: 'The Bull',
    dates: 'April 20 - May 20',
    element: 'Earth',
    modality: 'Fixed',
    ruler: 'Venus',
    icon: '♉',
    essence:
      'Taurus anchors experience in the body, the senses, and what can be built patiently over time.',
    strengths: 'steady, loyal, practical, sensual, reliable',
    tension: 'stubborn, slow to pivot, possessive, comfort-bound',
    relationships:
      'Taurus values consistency and emotional safety. Trust grows through follow-through, not dramatic declarations.',
    work:
      'Professionally, Taurus does best with tangible goals, sane pacing, and work that rewards craftsmanship, quality, and endurance.',
    growth:
      'Taurus grows by learning when to preserve and when to release. Stability becomes wisdom when it does not harden into resistance.',
    compatibility: 'Virgo, Capricorn, Cancer, and Pisces often feel grounding to Taurus.',
  },
  {
    slug: 'gemini',
    name: 'Gemini',
    symbol: 'The Twins',
    dates: 'May 21 - June 20',
    element: 'Air',
    modality: 'Mutable',
    ruler: 'Mercury',
    icon: '♊',
    essence:
      'Gemini gathers information, notices patterns, and keeps life in motion through language, curiosity, and exchange.',
    strengths: 'adaptable, witty, observant, social, inventive',
    tension: 'scattered, inconsistent, overthinking, restless',
    relationships:
      'Gemini bonds through conversation. Mental rapport, humor, and variety often matter as much as overt displays of emotion.',
    work:
      'Gemini shines in communication-heavy roles, strategy, teaching, media, research, and any environment where ideas evolve quickly.',
    growth:
      'The growth path for Gemini is depth. Not silence, but focus. Not certainty, but the ability to stay with a question long enough to transform.',
    compatibility: 'Libra, Aquarius, Aries, and Leo often match Gemini’s pace.',
  },
  {
    slug: 'cancer',
    name: 'Cancer',
    symbol: 'The Crab',
    dates: 'June 21 - July 22',
    element: 'Water',
    modality: 'Cardinal',
    ruler: 'Moon',
    icon: '♋',
    essence:
      'Cancer protects what feels sacred. It is the sign of memory, home, emotional intelligence, and intuitive care.',
    strengths: 'nurturing, perceptive, protective, devoted, imaginative',
    tension: 'defensive, moody, indirect, withdrawn',
    relationships:
      'Cancer loves by remembering. Safety, tenderness, and emotional reciprocity are non-negotiable.',
    work:
      'Cancer often excels where empathy and stewardship are strengths, especially in teaching, wellness, hospitality, management, or creative work.',
    growth:
      'Cancer grows by discovering that sensitivity is strongest when it includes boundaries. Care without containment turns into exhaustion.',
    compatibility: 'Scorpio, Pisces, Taurus, and Virgo often feel emotionally compatible.',
  },
  {
    slug: 'leo',
    name: 'Leo',
    symbol: 'The Lion',
    dates: 'July 23 - August 22',
    element: 'Fire',
    modality: 'Fixed',
    ruler: 'Sun',
    icon: '♌',
    essence:
      'Leo radiates identity. This sign is about creative self-expression, generosity, loyalty, and the courage to be seen.',
    strengths: 'warm, charismatic, brave, creative, protective',
    tension: 'prideful, dramatic, approval-seeking, rigid',
    relationships:
      'Leo needs heartfelt devotion and open appreciation. It offers large-hearted love and expects sincerity in return.',
    work:
      'Leo flourishes where presence matters: leadership, performance, entrepreneurship, branding, teaching, and client-facing work.',
    growth:
      'Leo matures when confidence stops depending on applause. The truest solar strength glows from integrity, not attention alone.',
    compatibility: 'Aries, Sagittarius, Gemini, and Libra often energize Leo.',
  },
  {
    slug: 'virgo',
    name: 'Virgo',
    symbol: 'The Maiden',
    dates: 'August 23 - September 22',
    element: 'Earth',
    modality: 'Mutable',
    ruler: 'Mercury',
    icon: '♍',
    essence:
      'Virgo refines. It observes what is working, what is wasteful, and what can be improved through care, discernment, and service.',
    strengths: 'analytical, diligent, helpful, precise, grounded',
    tension: 'perfectionistic, anxious, overly critical, cautious',
    relationships:
      'Virgo shows love through effort. It pays attention, solves problems, and notices what others overlook.',
    work:
      'Virgo is excellent in systems, editing, operations, health, research, and any field where detail protects quality.',
    growth:
      'Virgo’s lesson is that excellence is not the same as control. Healing begins when compassion joins precision.',
    compatibility: 'Taurus, Capricorn, Cancer, and Scorpio often complement Virgo well.',
  },
  {
    slug: 'libra',
    name: 'Libra',
    symbol: 'The Scales',
    dates: 'September 23 - October 22',
    element: 'Air',
    modality: 'Cardinal',
    ruler: 'Venus',
    icon: '♎',
    essence:
      'Libra studies balance, beauty, and relationship. It looks for harmony without losing sight of what is fair.',
    strengths: 'diplomatic, stylish, thoughtful, cooperative, perceptive',
    tension: 'indecisive, avoidant, image-conscious, conflict-averse',
    relationships:
      'Libra is partnership-oriented and usually happiest where dialogue, respect, and mutual grace are present.',
    work:
      'Libra often does well in design, law, negotiation, branding, advising, and any role where perspective and tact matter.',
    growth:
      'Libra deepens by understanding that peace built on self-abandonment is unstable. Real harmony includes honest friction.',
    compatibility: 'Gemini, Aquarius, Leo, and Sagittarius often sync with Libra.',
  },
  {
    slug: 'scorpio',
    name: 'Scorpio',
    symbol: 'The Scorpion',
    dates: 'October 23 - November 21',
    element: 'Water',
    modality: 'Fixed',
    ruler: 'Pluto and Mars',
    icon: '♏',
    essence:
      'Scorpio moves beneath the surface. It is the sign of intensity, transformation, trust, power, and emotional truth.',
    strengths: 'focused, fearless, intuitive, loyal, resilient',
    tension: 'guarded, controlling, suspicious, extreme',
    relationships:
      'Scorpio needs depth, honesty, and emotional bravery. Superficial intimacy rarely satisfies it for long.',
    work:
      'Scorpio often excels in research, psychology, crisis work, investigation, strategy, and any role requiring stamina and discretion.',
    growth:
      'Scorpio’s growth is learning that vulnerability is not weakness. Real power increases when control relaxes into trust.',
    compatibility: 'Cancer, Pisces, Virgo, and Capricorn often meet Scorpio at the right depth.',
  },
  {
    slug: 'sagittarius',
    name: 'Sagittarius',
    symbol: 'The Archer',
    dates: 'November 22 - December 21',
    element: 'Fire',
    modality: 'Mutable',
    ruler: 'Jupiter',
    icon: '♐',
    essence:
      'Sagittarius seeks meaning through movement. It chases wisdom, possibility, experience, and the freedom to expand.',
    strengths: 'optimistic, adventurous, philosophical, candid, spirited',
    tension: 'blunt, inconsistent, commitment-shy, overconfident',
    relationships:
      'Sagittarius wants both honesty and room to roam. Shared vision matters more than conventional romance scripts.',
    work:
      'Sagittarius thrives in travel, education, publishing, coaching, entrepreneurship, and growth-oriented environments.',
    growth:
      'Sagittarius becomes wiser when it learns that freedom without responsibility turns into drift. Meaning needs embodiment.',
    compatibility: 'Aries, Leo, Libra, and Aquarius often resonate with Sagittarius.',
  },
  {
    slug: 'capricorn',
    name: 'Capricorn',
    symbol: 'The Sea-Goat',
    dates: 'December 22 - January 19',
    element: 'Earth',
    modality: 'Cardinal',
    ruler: 'Saturn',
    icon: '♑',
    essence:
      'Capricorn is the architect of long-range effort. It values structure, mastery, resilience, and earned authority.',
    strengths: 'disciplined, strategic, dependable, ambitious, composed',
    tension: 'rigid, guarded, overly serious, work-fixated',
    relationships:
      'Capricorn is slow to trust, but deeply faithful once committed. It values respect, competence, and emotional steadiness.',
    work:
      'Capricorn excels in leadership, operations, finance, administration, engineering, and any field requiring durable results.',
    growth:
      'Capricorn grows by remembering that worth is not measured only by productivity. Rest and tenderness are part of the structure too.',
    compatibility: 'Taurus, Virgo, Scorpio, and Pisces often support Capricorn’s goals and depth.',
  },
  {
    slug: 'aquarius',
    name: 'Aquarius',
    symbol: 'The Water Bearer',
    dates: 'January 20 - February 18',
    element: 'Air',
    modality: 'Fixed',
    ruler: 'Uranus and Saturn',
    icon: '♒',
    essence:
      'Aquarius questions inherited assumptions and looks toward what comes next. It is social, future-facing, and conceptually bold.',
    strengths: 'original, independent, humane, inventive, objective',
    tension: 'detached, contrarian, unpredictable, emotionally distant',
    relationships:
      'Aquarius values friendship within love. It needs intellectual freedom, mutual respect, and enough space to think clearly.',
    work:
      'Aquarius often thrives in technology, reform, design systems, science, community building, and unconventional creative work.',
    growth:
      'Aquarius deepens when ideas reconnect with feeling. Vision becomes transformative when it stays accountable to human reality.',
    compatibility: 'Gemini, Libra, Sagittarius, and Aries often pair well with Aquarius.',
  },
  {
    slug: 'pisces',
    name: 'Pisces',
    symbol: 'The Fish',
    dates: 'February 19 - March 20',
    element: 'Water',
    modality: 'Mutable',
    ruler: 'Neptune and Jupiter',
    icon: '♓',
    essence:
      'Pisces dissolves boundaries and listens to subtle currents. It is imaginative, compassionate, symbolic, and deeply receptive.',
    strengths: 'empathetic, artistic, intuitive, forgiving, soulful',
    tension: 'escapist, vague, porous, idealistic, inconsistent',
    relationships:
      'Pisces wants emotional and spiritual resonance. It loves tenderness, imagination, and a sense of shared inner life.',
    work:
      'Pisces does well in healing, music, art, spiritual practice, service roles, and environments that reward intuition and empathy.',
    growth:
      'Pisces grows through grounding. Sensitivity becomes sustainable when dreams, intuition, and compassion are paired with clear limits.',
    compatibility: 'Cancer, Scorpio, Taurus, and Capricorn often feel natural with Pisces.',
  },
];

const majorArcana = [
  ['fool_tarot', 'The Fool', '0', 'holy spontaneity', 'reckless innocence', 'beginnings, trust, the call to leap'],
  ['magician_tarot', 'The Magician', 'I', 'focused will', 'manipulation or scattered power', 'skill, intention, manifestation'],
  ['high_priestess_tarot', 'The High Priestess', 'II', 'inner knowing', 'passivity or secrecy', 'intuition, hidden knowledge, stillness'],
  ['empress_tarot', 'The Empress', 'III', 'fertile creation', 'smothering excess', 'abundance, care, embodiment'],
  ['emperor_tarot', 'The Emperor', 'IV', 'wise structure', 'rigidity and domination', 'authority, order, stewardship'],
  ['hierophant_tarot', 'The Hierophant', 'V', 'living tradition', 'dogma and empty ritual', 'teaching, values, inherited wisdom'],
  ['lovers_tarot', 'The Lovers', 'VI', 'aligned union', 'misalignment and split desire', 'choice, intimacy, values-led connection'],
  ['chariot_tarot', 'The Chariot', 'VII', 'directed momentum', 'force without alignment', 'discipline, victory, determination'],
  ['strength_tarot', 'Strength', 'VIII', 'gentle courage', 'suppressed rage or shaky confidence', 'patience, heart, inner mastery'],
  ['hermit_tarot', 'The Hermit', 'IX', 'clarifying solitude', 'isolation and withdrawal', 'reflection, guidance, inward truth'],
  ['wheel_of_fortune_tarot', 'Wheel of Fortune', 'X', 'sacred timing', 'clinging to control', 'cycles, fate, turning points'],
  ['justice_tarot', 'Justice', 'XI', 'clear accountability', 'bias or avoidance', 'truth, ethics, consequence'],
  ['hanged_man_tarot', 'The Hanged Man', 'XII', 'surrendered perspective', 'stagnation or martyrdom', 'pause, reversal, release'],
  ['death_tarot', 'Death', 'XIII', 'transformative closure', 'fear of endings', 'transition, shedding, renewal'],
  ['temperance_tarot', 'Temperance', 'XIV', 'measured harmony', 'imbalance and excess', 'integration, healing, proportion'],
  ['devil_tarot', 'The Devil', 'XV', 'awareness of bondage', 'self-betrayal and compulsion', 'attachment, shadow, appetite'],
  ['tower_tarot', 'The Tower', 'XVI', 'liberating truth', 'resistance to necessary collapse', 'shock, revelation, upheaval'],
  ['star_tarot', 'The Star', 'XVII', 'renewed hope', 'discouragement and disconnection', 'faith, restoration, inspiration'],
  ['moon_tarot', 'The Moon', 'XVIII', 'symbolic intuition', 'confusion, projection, fear', 'dreams, mystery, uncertainty'],
  ['sun_tarot', 'The Sun', 'XIX', 'radiant clarity', 'inflated certainty', 'joy, vitality, recognition'],
  ['judgement_tarot', 'Judgement', 'XX', 'awakening and response', 'avoidance of calling', 'reckoning, forgiveness, vocation'],
  ['world_tarot', 'The World', 'XXI', 'integrated completion', 'unfinished cycles', 'wholeness, mastery, arrival'],
].map(([slug, name, numeral, gift, shadow, focus]) => ({
  slug,
  name,
  numeral,
  gift,
  shadow,
  focus,
}));

const lifePathNumbers = [
  ['life_path_1', 1, 'The Pioneer', 'independence and self-definition', 'self-centered force', 'building confidence through action'],
  ['life_path_2', 2, 'The Diplomat', 'sensitivity and collaboration', 'people-pleasing or indecision', 'creating peace without self-erasure'],
  ['life_path_3', 3, 'The Creator', 'expression and delight', 'scattered energy', 'turning inspiration into craft'],
  ['life_path_4', 4, 'The Builder', 'discipline and reliability', 'rigidity or overwork', 'creating sturdy foundations'],
  ['life_path_5', 5, 'The Explorer', 'freedom and adaptability', 'impulsiveness and avoidance', 'using change as a teacher'],
  ['life_path_6', 6, 'The Caretaker', 'responsibility and devotion', 'over-functioning and control', 'serving with healthier boundaries'],
  ['life_path_7', 7, 'The Seeker', 'insight and depth', 'withdrawal and skepticism', 'trusting both intellect and intuition'],
  ['life_path_8', 8, 'The Executive', 'power and stewardship', 'material obsession or harshness', 'using ambition with integrity'],
  ['life_path_9', 9, 'The Humanitarian', 'compassion and completion', 'savior patterns or burnout', 'offering wisdom without martyrdom'],
].map(([slug, number, archetype, gift, shadow, lesson]) => ({
  slug,
  number,
  archetype,
  gift,
  shadow,
  lesson,
}));

const chaldeanNumbers = [
  ['chaldean_number_1', 1, 'Solar leadership', 'decisive creative force', 'ego-driven urgency', 'learning to lead with steadiness'],
  ['chaldean_number_2', 2, 'Lunar receptivity', 'cooperation and emotional intelligence', 'hesitation and fragility', 'turning sensitivity into strength'],
  ['chaldean_number_3', 3, 'Jovian expression', 'optimism and articulation', 'dramatic excess', 'channeling inspiration productively'],
  ['chaldean_number_4', 4, 'Uranian disruption', 'originality and reform', 'rebellion without direction', 'building better systems instead of merely rejecting old ones'],
  ['chaldean_number_5', 5, 'Mercurial motion', 'adaptability and wit', 'nervous scattering', 'focusing change into usable skill'],
  ['chaldean_number_6', 6, 'Venusian harmony', 'care, beauty, and relationship', 'over-attachment to approval', 'balancing devotion with self-worth'],
  ['chaldean_number_7', 7, 'Neptunian mysticism', 'intuition and contemplation', 'escapism or detachment', 'grounding spiritual sensitivity'],
  ['chaldean_number_8', 8, 'Saturnian gravity', 'endurance and authority', 'heaviness or control', 'turning pressure into mastery'],
  ['chaldean_number_9', 9, 'Martian courage', 'action, magnetism, and protection', 'conflict or blunt force', 'using passion with purpose'],
].map(([slug, number, archetype, gift, shadow, lesson]) => ({
  slug,
  number,
  archetype,
  gift,
  shadow,
  lesson,
}));

const chineseAnimals = [
  ['chinese_zodiac_rat', 'Rat', 'resourceful beginnings', 'quick thinking and adaptability', 'anxious over-calculation', 'using intelligence without suspicion'],
  ['chinese_zodiac_ox', 'Ox', 'slow earned progress', 'persistence and integrity', 'rigidity and emotional reserve', 'staying open while remaining steady'],
  ['chinese_zodiac_tiger', 'Tiger', 'bold instinct', 'courage and charisma', 'impatience and volatility', 'tempering power with strategy'],
  ['chinese_zodiac_rabbit', 'Rabbit', 'graceful sensitivity', 'refinement and diplomacy', 'avoidance and fragility', 'protecting peace without shrinking'],
  ['chinese_zodiac_dragon', 'Dragon', 'visionary force', 'confidence and magnetism', 'pride and grandiosity', 'pairing ambition with humility'],
  ['chinese_zodiac_snake', 'Snake', 'measured intuition', 'wisdom and strategic calm', 'secrecy and possessiveness', 'trusting discernment without closing off'],
  ['chinese_zodiac_horse', 'Horse', 'free motion', 'independence and enthusiasm', 'restlessness and inconsistency', 'choosing freedom with direction'],
  ['chinese_zodiac_goat', 'Goat', 'gentle artistry', 'empathy and imagination', 'self-doubt and dependency', 'cultivating confidence alongside softness'],
  ['chinese_zodiac_monkey', 'Monkey', 'clever improvisation', 'inventiveness and play', 'trickiness and distraction', 'using brilliance with sincerity'],
  ['chinese_zodiac_rooster', 'Rooster', 'disciplined expression', 'precision and honesty', 'criticism and perfectionism', 'letting excellence include warmth'],
  ['chinese_zodiac_dog', 'Dog', 'protective loyalty', 'fairness and devotion', 'worry and defensiveness', 'keeping faith without cynicism'],
  ['chinese_zodiac_pig', 'Pig', 'abundant goodwill', 'generosity and pleasure', 'overindulgence and naivete', 'enjoying life without losing discernment'],
].map(([slug, name, essence, gift, shadow, lesson]) => ({
  slug,
  name,
  essence,
  gift,
  shadow,
  lesson,
}));

const suits = {
  cups: {
    slug: 'cups',
    name: 'Cups',
    icon: '☽',
    element: 'Water',
    domain: 'emotion, relationship, memory, intuition',
    focus: 'how the heart receives, bonds, grieves, and heals',
  },
  wands: {
    slug: 'wands',
    name: 'Wands',
    icon: '✦',
    element: 'Fire',
    domain: 'desire, initiative, purpose, momentum',
    focus: 'how energy becomes action, ambition, and creative direction',
  },
  swords: {
    slug: 'swords',
    name: 'Swords',
    icon: '⚔',
    element: 'Air',
    domain: 'thought, language, truth, conflict',
    focus: 'how the mind interprets reality and how clarity can both heal and cut',
  },
  pentacles: {
    slug: 'pentacles',
    name: 'Pentacles',
    icon: '◆',
    element: 'Earth',
    domain: 'resources, work, body, craft, stability',
    focus: 'how ideas become material through effort, stewardship, and time',
  },
};

const ranks = [
  ['ace', 'Ace', 'a seed stage', 'raw potential asking for a beginning'],
  ['two', 'Two', 'a balancing stage', 'partnership, contrast, and early adjustment'],
  ['three', 'Three', 'a building stage', 'collaboration, skill, and visible development'],
  ['four', 'Four', 'a stabilizing stage', 'structure, rest, or consolidation'],
  ['five', 'Five', 'a disruptive stage', 'friction, conflict, or change that exposes truth'],
  ['six', 'Six', 'a harmonizing stage', 'recovery, reciprocity, or momentum after strain'],
  ['seven', 'Seven', 'a testing stage', 'strategy, faith, evaluation, or persistence'],
  ['eight', 'Eight', 'an intensifying stage', 'mastery, movement, focus, or pressure'],
  ['nine', 'Nine', 'a ripening stage', 'culmination, solitude, resilience, or fulfillment'],
  ['ten', 'Ten', 'a completion stage', 'overflow, legacy, burden, or final consequence'],
  ['page', 'Page', 'an apprentice stage', 'curiosity, fresh learning, and a message entering awareness'],
  ['knight', 'Knight', 'an activating stage', 'pursuit, motion, and concentrated drive'],
  ['queen', 'Queen', 'an integrating stage', 'embodied mastery, maturity, and inner authority'],
  ['king', 'King', 'a governing stage', 'external command, responsibility, and seasoned leadership'],
].map(([slug, name, phase, lesson]) => ({ slug, name, phase, lesson }));

const blogPosts = [
  {
    path: '/blog/how-to-read-tarot-for-yourself',
    title: 'How To Read Tarot for Yourself Without Flattening the Message',
    description:
      'A grounded tarot workflow for solo readings that keeps symbolism, context, and reflection in balance.',
    intro:
      'Reading tarot for yourself is harder than memorizing keywords. The real work is learning how to stay honest when you are both the reader and the person with skin in the game.',
    sections: [
      {
        title: 'Start with the question, not the spread',
        body: [
          'Good readings begin with a useful question. Vague prompts invite vague answers. A focused question gives each card a job and keeps the reading from becoming a collage of disconnected meanings.',
          'Questions that work well usually name a circumstance, a tension, or a decision. They ask for perspective instead of certainty. Tarot is strongest when it helps you interpret a situation more clearly, not when it pretends to remove free will.',
        ],
      },
      {
        title: 'Read the pattern before the card meanings',
        body: [
          'Before diving into card-by-card definitions, look at the overall weather of the spread. Are most cards from the same suit? Is the energy heavy with Major Arcana? Are there repeating numbers or mirrored themes?',
          'Patterns often tell the truth faster than isolated keywords. They show whether the reading is about emotion, conflict, timing, structure, or a major turning point in your development.',
        ],
      },
      {
        title: 'Use reflection, not panic',
        body: [
          'The cards do not punish honest readers. Even intense cards like Death, The Tower, or Ten of Swords become useful when you ask what is ending, what is unsustainable, or what truth wants to be acknowledged.',
          'A strong self-reading closes with one or two grounded actions: a conversation to have, a limit to set, a practice to begin, or a perspective to revisit in a week.',
        ],
      },
    ],
  },
  {
    path: '/blog/major-arcana-vs-minor-arcana',
    title: 'Major Arcana vs. Minor Arcana: What the Difference Actually Changes',
    description:
      'A practical guide to understanding when tarot points to a defining life lesson versus a situational daily pattern.',
    intro:
      'Readers often learn that the Major Arcana feels bigger and the Minor Arcana feels more everyday. That is true, but the distinction becomes much more useful when you understand what kind of change each group is tracking.',
    sections: [
      {
        title: 'Major Arcana marks the lesson',
        body: [
          'The Major Arcana speaks in archetypes. These cards often show up when identity, worldview, timing, or a defining life threshold is at stake. They do not merely describe an event; they frame the meaning of the event.',
          'When a reading is heavy with Major Arcana, it often points to a period you will remember as formative. The lesson may not feel dramatic in the moment, but it usually has long-range consequences.',
        ],
      },
      {
        title: 'Minor Arcana shows the lived texture',
        body: [
          'The Minor Arcana describes how life is moving through emotion, thought, effort, and desire right now. These cards explain the atmosphere of a week, a project, a conversation, or a habit pattern.',
          'Minor cards often reveal the practical pressure points around a major lesson. They show what the archetype looks like on a Tuesday afternoon.',
        ],
      },
      {
        title: 'The two work best together',
        body: [
          'A rich reading usually contains both. The Major Arcana names the chapter. The Minor Arcana describes the scenes, choices, and recurring reactions inside that chapter.',
          'Learning to read them together keeps your interpretations balanced: archetypal enough to matter, practical enough to use.',
        ],
      },
    ],
  },
  {
    path: '/blog/what-your-life-path-number-cant-do',
    title: 'What Your Life Path Number Can and Cannot Tell You',
    description:
      'A clearer way to use numerology without turning one number into a fixed identity.',
    intro:
      'Life Path numbers are useful because they describe recurring themes, motivations, and lessons. They become less useful when they are treated like destiny in a cage.',
    sections: [
      {
        title: 'What the Life Path does well',
        body: [
          'A Life Path number gives language to your preferred mode of growth. It can explain why certain challenges repeat, what kind of work feels meaningful, and where your strengths become most visible.',
          'It is especially helpful when used as a reflection tool. The number does not replace self-knowledge; it gives it structure.',
        ],
      },
      {
        title: 'What it cannot do',
        body: [
          'A Life Path number cannot summarize your whole personality, choose the right partner for you, or override context, upbringing, and free will. Two people with the same Life Path can live it in very different ways.',
          'Numerology becomes distorted when it turns complexity into branding. Real spiritual tools expand nuance instead of reducing it.',
        ],
      },
      {
        title: 'Use it as a compass',
        body: [
          'The most grounded approach is to ask how your number behaves when you are thriving and how it behaves when you are stressed. That difference reveals the practice.',
          'A useful numerology system should make your choices clearer, your habits more visible, and your growth more intentional.',
        ],
      },
    ],
  },
];

const editorialFaqs = [
  {
    question: 'Are tarot, astrology, and numerology factual sciences?',
    answer:
      'Cosmic Secrets approaches these systems as symbolic frameworks for reflection, storytelling, and personal meaning rather than as scientific disciplines.',
  },
  {
    question: 'How often are the guides updated?',
    answer:
      'Core guide pages are reviewed and refreshed whenever the explanations need to be clearer, deeper, or more useful. This version was last updated on May 4, 2026.',
  },
  {
    question: 'Do I need to agree to cookies to use the site?',
    answer:
      'No. The site works without optional analytics cookies. Consent only affects non-essential tracking.',
  },
];

function createBreadcrumb(path, title) {
  if (path === '/') {
    return [{ name: 'Home', path: '/' }];
  }

  const trail = [{ name: 'Home', path: '/' }];

  if (path.startsWith('/tarot') || path.includes('_tarot') || ['wands', 'cups', 'swords', 'pentacles', 'major_arcana', 'minor_arcana', 'fools_journey'].some((segment) => path.includes(segment))) {
    trail.push({ name: 'Tarot', path: '/tarot' });
  }

  if (path.startsWith('/zodiac') || zodiacSigns.some((sign) => path === `/${sign.slug}`)) {
    trail.push({ name: 'Zodiac', path: '/zodiac' });
  }

  if (path.startsWith('/numerology') || path.includes('life_path') || path.includes('chaldean_number') || path === '/pythagorean_numerology' || path === '/chaldean_numerology' || path === '/kabbalistic_numerology' || path === '/chinese_numerology') {
    trail.push({ name: 'Numerology', path: '/numerology' });
  }

  if (path.startsWith('/chinese_zodiac')) {
    trail.push({ name: 'Chinese Zodiac', path: '/chinese_zodiac' });
  }

  if (path.startsWith('/blog')) {
    trail.push({ name: 'Blog', path: '/blog' });
  }

  if (trail[trail.length - 1]?.path !== path) {
    trail.push({ name: title, path });
  }

  return trail;
}

function routePath(slug) {
  return slug.startsWith('/') ? slug : `/${slug}`;
}

function estimateReadingTime(page) {
  const text = [
    page.description,
    page.intro,
    ...(page.sections || []).flatMap((section) => [section.title, ...(section.body || [])]),
    ...(page.faqs || []).flatMap((faq) => [faq.question, faq.answer]),
  ]
    .filter(Boolean)
    .join(' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(4, Math.round(words / 180));
}

function createPage(page) {
  const fullPath = routePath(page.path);
  const canonicalPath = routePath(page.canonicalPath || fullPath);
  const displayTitle = page.title;
  const seoTitle = page.seoTitle || `${displayTitle} | ${SITE.name}`;
  const breadcrumbs = page.breadcrumbs || createBreadcrumb(fullPath, page.title);

  return {
    ...page,
    path: fullPath,
    canonicalPath,
    canonicalUrl: `${SITE.url}${canonicalPath}`,
    title: displayTitle,
    seoTitle,
    readingTime: estimateReadingTime(page),
    breadcrumbs,
    updated: page.updated || SITE.updated,
    image: page.image || SITE.defaultImage,
  };
}

function makeArticle(page) {
  return createPage({
    kind: 'article',
    ...page,
  });
}

function makeCollection(page) {
  return createPage({
    kind: 'collection',
    ...page,
  });
}

function createZodiacPage(sign) {
  return makeArticle({
    path: `/${sign.slug}`,
    title: `${sign.name}: personality, compatibility, career, and growth`,
    seoTitle: `${sign.name} Zodiac Sign Meaning: Traits, Compatibility, Career, and Growth | ${SITE.name}`,
    description: `${sign.name} explained in depth: dates, element, ruling planet, personality patterns, relationships, career style, and spiritual growth.`,
    eyebrow: `${sign.icon} ${sign.name} • ${sign.symbol}`,
    heroSummary: sign.essence,
    intro: `${sign.name} is often reduced to a handful of personality buzzwords, but every zodiac sign is more interesting than its stereotypes. At its best, ${sign.name} expresses ${sign.gift || sign.strengths}. At its most stressed, the same energy can become ${sign.tension}. Reading the sign well means understanding both the instinct and the lesson.`,
    facts: [
      ['Dates', sign.dates],
      ['Element', sign.element],
      ['Modality', sign.modality],
      ['Ruler', sign.ruler],
    ],
    image: zodiacImageMap[sign.slug],
    imageAlt: `${sign.name} zodiac artwork`,
    sections: [
      {
        title: `${sign.name} core temperament`,
        body: [
          `${sign.essence} In practice, this shows up as a recognizable rhythm: ${sign.name} tends to approach life through the lens of ${sign.element.toLowerCase()} energy and ${sign.modality.toLowerCase()} momentum.`,
          `The strongest version of ${sign.name} is usually ${sign.strengths}. The difficult side appears when pressure builds and those strengths harden into ${sign.tension}.`,
        ],
      },
      {
        title: `${sign.name} strengths and natural gifts`,
        body: [
          `${sign.name} usually feels most alive when it is allowed to express its natural style without apology. That may show up as leadership, steadiness, wit, devotion, magnetism, or imagination depending on the sign, but the deeper pattern is the same: the sign wants to function in alignment with its own nature.`,
          `Because of that, the healthiest expression of ${sign.name} is rarely performative. It feels grounded, self-trusting, and useful. The sign becomes especially compelling when its gifts are channeled into something that benefits both the self and the people around it.`,
        ],
      },
      {
        title: `${sign.name} shadow traits and emotional blind spots`,
        body: [
          `Every sign has a pressure response. Under stress, ${sign.name} can become ${sign.tension}. These reactions are not random flaws. They are the sign's strengths becoming overdriven, unexamined, or pushed into defensiveness.`,
          `That is why shadow work matters. Instead of trying to become a different sign, the work is to recognize when instinct has stopped being wisdom. Once that shift is visible, ${sign.name} can keep its force while losing some of the collateral damage.`,
        ],
      },
      {
        title: `${sign.name} in love, friendship, and trust`,
        body: [
          sign.relationships,
          `${sign.name} tends to feel safest with people who understand the sign’s tempo. ${sign.compatibility} Compatibility is less about perfection and more about whether both people respect the emotional logic that ${sign.name} naturally follows.`,
        ],
      },
      {
        title: `${sign.name} communication style and relationships`,
        body: [
          `In everyday relationships, ${sign.name} communicates through tone as much as words. Some signs are straightforward and immediate, while others process emotionally before they speak. Understanding that rhythm can prevent a lot of unnecessary misunderstanding.`,
          `This is especially important in long-term connection. ${sign.name} is at its best with people who can read the difference between a passing reaction and a core value. Once that trust is built, the sign usually becomes far more generous, loyal, and emotionally legible.`,
        ],
      },
      {
        title: `${sign.name} at work and with ambition`,
        body: [
          sign.work,
          `Because ${sign.name} usually wants life to feel coherent from the inside out, professional success tends to grow when the work environment honors the sign’s native strengths rather than punishing them.`,
        ],
      },
      {
        title: `${sign.name} and long-term growth`,
        body: [
          `${sign.growth} Growth for ${sign.name} is rarely about becoming milder or more socially acceptable. It is about becoming more conscious in the use of its own energy.`,
          `That maturity is what makes the sign powerful. Once ${sign.name} learns how to work with its own instincts instead of being dragged around by them, the gifts of the sign become much more stable and much more useful.`,
        ],
      },
      {
        title: `A deeper reading of ${sign.name}`,
        body: [
          `The real value of studying ${sign.name} is not collecting adjectives. It is learning how a specific kind of human energy behaves when it is thriving, when it is frightened, and when it is being asked to evolve.`,
          `That is why the sign’s real wisdom is never the stereotype alone. ${sign.name} becomes more legible, more nuanced, and more useful the longer you stay with the pattern.`,
        ],
      },
    ],
    related: [
      '/zodiac',
      '/tarot',
      '/numerology',
      `/${zodiacSigns[(zodiacSigns.findIndex((item) => item.slug === sign.slug) + 11) % 12].slug}`,
      `/${zodiacSigns[(zodiacSigns.findIndex((item) => item.slug === sign.slug) + 1) % 12].slug}`,
    ],
  });
}

function createMajorArcanaPage(card) {
  return makeArticle({
    path: `/${card.slug}`,
    title: `${card.name} tarot meaning`,
    seoTitle: `${card.name} Tarot Meaning: Symbolism, Guidance, and Shadow | ${SITE.name}`,
    description: `${card.name} explained in depth with symbolism, core meaning, shadow lessons, and how the card behaves in personal and practical readings.`,
    eyebrow: `Tarot • Major Arcana ${card.numeral}`,
    heroSummary: `${card.name} is a card of ${card.focus}. Its gift is ${card.gift}; its shadow is ${card.shadow}.`,
    intro: `The Major Arcana cards describe life lessons that feel bigger than a passing mood. ${card.name} often appears when a situation is asking for a deeper shift in perspective, identity, or timing.`,
    facts: [
      ['Arcana', 'Major Arcana'],
      ['Number', card.numeral],
      ['Gift', card.gift],
      ['Shadow', card.shadow],
    ],
    image: majorArcanaImageMap[card.slug],
    imageAlt: `${card.name} tarot card`,
    sections: [
      {
        title: `What ${card.name} represents`,
        body: [
          `${card.name} concentrates the theme of ${card.focus}. When it lands in a reading, it usually names the central lesson of the moment rather than a small passing detail.`,
          `This is why the card is best read as an invitation to work consciously with its energy. The point is not to memorize a slogan, but to understand what your life is being asked to practice.`,
        ],
      },
      {
        title: `${card.name} symbolism and tone`,
        body: [
          `Even without a deck in front of you, the archetype of ${card.name} carries a clear emotional signature. Its brighter expression points toward ${card.gift}. Its more difficult expression can look like ${card.shadow}.`,
          `The card becomes especially useful when you ask where that tension is visible in your current situation. Symbolism matters because it translates inner dynamics into something you can actually notice.`,
        ],
      },
      {
        title: `${card.name} when the energy is healthy`,
        body: [
          `In a balanced reading, ${card.name} often signals a chance to embody its higher expression rather than merely admire it. The card asks for participation. It wants behavior, not just interpretation.`,
          `That may mean choosing honesty, discipline, surrender, courage, or patience depending on the card. What matters is that the reading points toward a wiser use of energy rather than empty reassurance.`,
        ],
      },
      {
        title: `${card.name} in its shadow form`,
        body: [
          `Every Major Arcana card has a difficult side. Sometimes the shadow appears because the lesson is being resisted. Other times it appears because the energy is being exaggerated or performed without depth.`,
          `Reading the shadow side well can be more useful than reading the gift. It shows what the situation becomes when fear, ego, avoidance, or overcontrol starts leading the story.`,
        ],
      },
      {
        title: `${card.name} in love and relationships`,
        body: [
          `In relationship readings, ${card.name} usually points to the quality of the lesson the connection is living through. Some cards ask for intimacy and truth, while others expose illusion, power struggle, timing, or a need for more conscious choice.`,
          `The practical question is not whether the card is "good" or "bad." It is whether the relationship is using the card's lesson to deepen, or whether it is getting trapped in the card's shadow.`,
        ],
      },
      {
        title: `${card.name} in work, direction, and decision-making`,
        body: [
          `Career readings tend to make the Major Arcana especially clear. ${card.name} often appears when the issue is larger than a temporary job question. It can mark vocation, pressure, authority, burnout, reinvention, visibility, or a threshold that changes how the work is understood.`,
          `That is why this card should not be flattened into career advice alone. It often reveals what kind of inner posture is required before the outer decision will make sense.`,
        ],
      },
      {
        title: `${card.name} in a practical reading`,
        body: [
          `In relationships, work, or personal growth, ${card.name} often appears when the issue cannot be solved by a minor tactic alone. The real change is structural, emotional, or spiritual.`,
          `A grounded reading asks: what action reflects the wisdom of ${card.gift}, and what habit keeps pulling the situation toward ${card.shadow}? That question usually opens the card faster than fortune-telling language.`,
        ],
      },
      {
        title: `The deeper lesson of ${card.name}`,
        body: [
          `Major Arcana cards become memorable because they echo long after a reading ends. ${card.name} stays relevant until its lesson has been integrated, not merely noticed.`,
          `That makes this card less about prediction and more about participation. The archetype reveals what kind of maturity is being asked of you now.`,
        ],
      },
    ],
    related: ['/major_arcana', '/fools_journey', '/tarot'],
  });
}

function createSuitHubPage(suit) {
  const cards = ranks.map((rank) => routePath(`${rank.slug}_of_${suit.slug}`));
  return makeCollection({
    path: `/${suit.slug}`,
    title: `${suit.name} tarot meaning`,
    seoTitle: `${suit.name} Tarot Meaning: ${suit.element} Suit Guide | ${SITE.name}`,
    description: `${suit.name} in tarot explained: suit element, emotional territory, card-by-card links, and how the suit behaves in readings.`,
    eyebrow: `Tarot Suit • ${suit.element}`,
    heroSummary: `${suit.name} governs ${suit.domain}. This suit shows ${suit.focus}.`,
    intro: `${suit.name} is one of the four suits of the Minor Arcana. When several ${suit.name} cards appear together, the reading is usually focused on ${suit.domain}.`,
    image: minorArcanaImage(suit.slug, 'ace'),
    imageAlt: `${suit.name} tarot card spread`,
    cards: cards.map((path, index) => ({
      path,
      title: `${ranks[index].name} of ${suit.name}`,
      summary: `${ranks[index].phase} focused on ${suit.domain}.`,
      icon: suit.icon,
      image: minorArcanaImage(suit.slug, ranks[index].slug),
      imageAlt: `${ranks[index].name} of ${suit.name} tarot card`,
    })),
    sections: [
      {
        title: `How to read the suit of ${suit.name}`,
        body: [
          `${suit.name} belongs to the element of ${suit.element}. In practical readings, that means the suit often points to ${suit.domain}.`,
          `A useful way to read it is to combine the suit with the rank. The suit tells you the arena of life; the rank tells you the stage of development inside that arena.`,
        ],
      },
      {
        title: `${suit.name} in real readings`,
        body: [
          `When this suit dominates a spread, the reading is not random. It is asking you to pay attention to ${suit.focus}.`,
          `The cards below break that story into stages so you can read the suit with more nuance than a one-line keyword.`,
        ],
      },
      {
        title: `${suit.name} at its highest expression`,
        body: [
          `Every suit has a constructive form. ${suit.name} at its best helps a reader understand the healthy use of ${suit.domain}. The cards show movement, not just static personality.`,
          `This is what makes the suit valuable for self-reflection. It reveals how a person is handling the arena of life that the suit rules, whether that is emotion, thought, effort, or desire.`,
        ],
      },
      {
        title: `${suit.name} in its more difficult form`,
        body: [
          `When the suit's energy is blocked, overused, or distorted, the cards become more uncomfortable and more useful. They show exactly where the pattern has tightened.`,
          `Rather than treating that as bad news, it helps to read it as information. The suit is telling you where the work actually is.`,
        ],
      },
    ],
    related: ['/minor_arcana', '/tarot'],
  });
}

function createMinorArcanaPage(suit, rank) {
  const title = `${rank.name} of ${suit.name}`;
  return makeArticle({
    path: `/${rank.slug}_of_${suit.slug}`,
    title: `${title} tarot meaning`,
    seoTitle: `${title} Tarot Meaning: Interpretation, Shadow, and Use | ${SITE.name}`,
    description: `${title} explained through suit symbolism, rank progression, emotional tone, practical interpretation, and reflection prompts.`,
    eyebrow: `Tarot • ${suit.name} • ${rank.name}`,
    heroSummary: `${title} combines ${suit.domain} with ${rank.phase}. The result is a card about ${rank.lesson}.`,
    intro: `${title} belongs to the Minor Arcana, where everyday experience becomes readable through symbols. This card is most useful when you combine the ${suit.name.toLowerCase()} suit's territory with the rank's developmental stage.`,
    facts: [
      ['Suit', suit.name],
      ['Element', suit.element],
      ['Stage', rank.phase],
      ['Focus', rank.lesson],
    ],
    image: minorArcanaImage(suit.slug, rank.slug),
    imageAlt: `${title} tarot card`,
    sections: [
      {
        title: `Core meaning of the ${title}`,
        body: [
          `${title} usually points toward ${rank.lesson} in the realm of ${suit.domain}. That makes it a card about process, not just outcome.`,
          `The card often appears when the situation is teaching you how to relate to ${suit.focus} with more maturity and specificity.`,
        ],
      },
      {
        title: `${title} symbolism and elemental tone`,
        body: [
          `The ${suit.name.toLowerCase()} suit always colors the card with the element of ${suit.element.toLowerCase()}. That means the emotional tone of the card is never separate from the part of life it is addressing.`,
          `The rank adds the developmental stage. ${rank.name} represents ${rank.phase}, so the card reads as a very particular moment inside the larger story of ${suit.domain}.`,
        ],
      },
      {
        title: `How the ${title} behaves when balanced`,
        body: [
          `In its balanced expression, this card helps you work constructively with ${suit.domain}. It suggests that the lesson of ${rank.phase} can be embodied instead of merely understood.`,
          `That may look like better pacing, cleaner boundaries, a more honest conversation, or a stronger commitment to the work the card is naming.`,
        ],
      },
      {
        title: `The shadow side of the ${title}`,
        body: [
          `Every tarot card has a distorted form. Here, the distortion often shows up when ${rank.lesson} is avoided, rushed, or over-controlled.`,
          `That is why the card reads best when you ask not only what it means, but where its energy has become either blocked or excessive.`,
        ],
      },
      {
        title: `${title} in love and relationships`,
        body: [
          `In relationship readings, the ${title} usually describes a stage of emotional, mental, practical, or energetic development between people. It can point to a dynamic that is growing, fraying, stabilizing, or asking to be acknowledged more honestly.`,
          `The best way to read it is contextually. Ask what part of the relationship is living out the lesson of this card and whether that lesson is being handled skillfully or reactively.`,
        ],
      },
      {
        title: `${title} in work, money, and decisions`,
        body: [
          `In practical readings, this card often becomes very clear. It may show how someone is managing effort, conflict, communication, risk, intuition, money, or emotional labor depending on the suit.`,
          `That makes the card useful for decision-making. It shows the shape of the process you are in, not just the surface event that has your attention.`,
        ],
      },
      {
        title: `Reading the ${title} in context`,
        body: [
          `In love, this card changes meaning depending on whether the surrounding spread supports openness or defensiveness. In work, it often points to how you are handling ${suit.domain} in real time.`,
          `Use the neighboring cards to refine the story, but let ${title} keep its central job: showing how this stage of ${suit.name.toLowerCase()} development is playing out now.`,
        ],
      },
      {
        title: `What the ${title} asks of you`,
        body: [
          `The most useful question with any Minor Arcana card is simple: what would a more skillful version of this energy look like in my life right now? That question turns interpretation into action.`,
          `The ${title} becomes richer over time because it keeps answering that question differently as your circumstances change.`,
        ],
      },
    ],
    related: [`/${suit.slug}`, '/minor_arcana', '/tarot'],
  });
}

function createLifePathPage(entry) {
  return makeArticle({
    path: `/${entry.slug}`,
    title: `Life Path ${entry.number} meaning`,
    seoTitle: `Life Path ${entry.number} Meaning: Personality, Work, and Growth | ${SITE.name}`,
    description: `Life Path ${entry.number} explained with gifts, challenges, relationship style, work patterns, and the deeper lesson behind the number.`,
    eyebrow: `Numerology • Life Path ${entry.number}`,
    heroSummary: `Life Path ${entry.number} is the path of ${entry.archetype.toLowerCase()}. Its gift is ${entry.gift}; its shadow is ${entry.shadow}.`,
    intro: `Life Path numbers are best understood as developmental themes rather than rigid labels. Life Path ${entry.number} describes the pattern of growth that tends to shape a person’s motivations, strengths, and recurring lessons.`,
    facts: [
      ['Number', `${entry.number}`],
      ['Archetype', entry.archetype],
      ['Gift', entry.gift],
      ['Shadow', entry.shadow],
    ],
    image: lifePathImageMap[entry.number],
    imageAlt: `Life Path ${entry.number} illustration`,
    sections: [
      {
        title: `The core of Life Path ${entry.number}`,
        body: [
          `At its healthiest, Life Path ${entry.number} moves through life with a natural pull toward ${entry.gift}. That tendency often feels obvious early on, even if it is not yet refined.`,
          `The deeper invitation is ${entry.lesson}. Life Path work becomes meaningful when the gift is practiced with maturity instead of relying on instinct alone.`,
        ],
      },
      {
        title: `Life Path ${entry.number} strengths`,
        body: [
          `One reason Life Path numbers resonate with people is that they often name a strength pattern that feels familiar across many years of life. The number shows where confidence tends to build naturally and where purpose often feels easiest to access.`,
          `For Life Path ${entry.number}, that strength pattern is tied to ${entry.gift}. When expressed well, it can become a major source of steadiness, contribution, and direction.`,
        ],
      },
      {
        title: `The shadow side of Life Path ${entry.number}`,
        body: [
          `No Life Path is all gift. Under stress, the same instincts that make the number effective can turn into ${entry.shadow}. That is not a contradiction. It is the normal shadow of an energy used without enough reflection.`,
          `This is why the number should never be read as flattery. Its value lies in showing both where you naturally shine and where your habits may quietly work against you.`,
        ],
      },
      {
        title: `Relationships and emotional patterns`,
        body: [
          `In relationships, Life Path ${entry.number} usually wants to feel seen for what it is trying to build or protect. That does not make the number inflexible, but it does mean connection works best when its central values are understood.`,
          `The shadow side can appear as ${entry.shadow}. Naming that pattern early often keeps it from running the relationship in the background.`,
        ],
      },
      {
        title: `Career, work, and practical growth`,
        body: [
          `Professionally, Life Path ${entry.number} often does best where its central drive can become useful in the real world. The number tends to thrive when responsibility, expression, or strategy align with its native rhythm.`,
          `The point is not to choose a career solely because of numerology. It is to understand the style of work that makes your effort sustainable and meaningful.`,
        ],
      },
      {
        title: `How Life Path ${entry.number} matures`,
        body: [
          `The number becomes more interesting with age. Early in life it may appear as instinct. Later it becomes a conscious practice. That evolution is where the real depth of numerology lives.`,
          `For Life Path ${entry.number}, maturity usually means ${entry.lesson}. Once that lesson is embodied, the number often feels less dramatic and much more quietly powerful.`,
        ],
      },
    ],
    related: ['/numerology', '/pythagorean_numerology'],
  });
}

function createChaldeanPage(entry) {
  return makeArticle({
    path: `/${entry.slug}`,
    title: `Chaldean number ${entry.number} meaning`,
    seoTitle: `Chaldean Number ${entry.number} Meaning: Traits, Shadow, and Lesson | ${SITE.name}`,
    description: `Chaldean number ${entry.number} explained with symbolic archetype, strengths, challenges, and how to work with its energy in daily life.`,
    eyebrow: `Numerology • Chaldean Number ${entry.number}`,
    heroSummary: `Chaldean number ${entry.number} carries the archetype of ${entry.archetype.toLowerCase()}. Its gift is ${entry.gift}; its shadow is ${entry.shadow}.`,
    intro: `Chaldean numerology reads numbers as symbolic frequencies rather than as simple arithmetic. Number ${entry.number} is most useful when you treat it as a pattern of emphasis: what comes naturally, what becomes distorted under stress, and what the soul seems to be practicing through repetition.`,
    facts: [
      ['Number', `${entry.number}`],
      ['Archetype', entry.archetype],
      ['Gift', entry.gift],
      ['Shadow', entry.shadow],
    ],
    image: chaldeanImageMap[entry.number],
    imageAlt: `Chaldean number ${entry.number} illustration`,
    sections: [
      {
        title: `The essence of Chaldean number ${entry.number}`,
        body: [
          `At its strongest, this number expresses ${entry.gift}. It often shows up through temperament, motivation, and the style with which a person approaches challenge.`,
          `Its longer lesson is ${entry.lesson}. That is what keeps the number from becoming a static label and turns it into something developmental.`,
        ],
      },
      {
        title: `The gift of Chaldean number ${entry.number}`,
        body: [
          `Chaldean numerology is especially useful when you want to understand tone. Some numbers feel bright and direct. Others feel subtle, magnetic, heavy, or unusually interior. That tonal quality is often what readers notice first.`,
          `Number ${entry.number} carries its own unmistakable flavor. When a person works well with it, the result can be distinctive, memorable, and deeply effective.`,
        ],
      },
      {
        title: `Where the number becomes distorted`,
        body: [
          `When pressure is high, the same energy can twist into ${entry.shadow}. This is why numerology is most helpful as a mirror for patterns rather than a prediction machine.`,
          `Seeing the distortion clearly gives you a chance to redirect the energy instead of repeating it unconsciously.`,
        ],
      },
      {
        title: `Relationships, identity, and tone`,
        body: [
          `In personal life, this number often shapes how a person is felt by others before it shapes what they consciously intend. That is part of why Chaldean readings can feel strangely accurate: they speak to atmosphere as much as behavior.`,
          `When the number is integrated, its tone becomes coherent and attractive. When it is stressed, people may experience the distortion before they understand the gift.`,
        ],
      },
      {
        title: `Working with the number in everyday life`,
        body: [
          `A grounded way to work with this number is to notice where its strengths already appear naturally, then create habits that support the lesson instead of the shadow.`,
          `That makes the number practical. It becomes a tool for self-observation, decision-making, and better timing rather than mystical branding.`,
        ],
      },
      {
        title: `Why the number remains useful`,
        body: [
          `The more you work with symbolic systems, the more useful nuance becomes. Chaldean number ${entry.number} stays interesting because it points to a whole pattern rather than a one-line personality tag.`,
          `Read that way, the number becomes less about certainty and more about insight. It helps you notice the kind of energy you are amplifying every day.`,
        ],
      },
    ],
    related: ['/chaldean_numerology', '/numerology'],
  });
}

function createChineseZodiacPage(entry) {
  return makeArticle({
    path: `/${entry.slug}`,
    title: `${entry.name} in the Chinese zodiac`,
    seoTitle: `${entry.name} Chinese Zodiac Meaning: Traits, Shadow, and Lesson | ${SITE.name}`,
    description: `${entry.name} in the Chinese zodiac explained with temperament, strengths, shadow patterns, and a grounded interpretation of the animal’s symbolism.`,
    eyebrow: `Chinese Zodiac • ${entry.name}`,
    heroSummary: `${entry.name} symbolizes ${entry.essence}. Its gift is ${entry.gift}; its shadow is ${entry.shadow}.`,
    intro: `Chinese zodiac readings work best when the animal is treated as a symbolic temperament rather than a cartoon personality. ${entry.name} describes a recognizable style of response, motivation, and growth.`,
    facts: [
      ['Animal', entry.name],
      ['Essence', entry.essence],
      ['Gift', entry.gift],
      ['Shadow', entry.shadow],
    ],
    image: chineseZodiacImageMap[entry.name],
    imageAlt: `${entry.name} Chinese zodiac artwork`,
    sections: [
      {
        title: `What ${entry.name} represents`,
        body: [
          `${entry.name} is associated with ${entry.essence}. In lived experience, that often shows up as a natural bias toward ${entry.gift}.`,
          `The symbol matters because it names a style of intelligence. Rather than asking whether the animal is "accurate," the better question is whether its pattern helps you see yourself more clearly.`,
        ],
      },
      {
        title: `${entry.name} strengths and temperament`,
        body: [
          `Every animal in the Chinese zodiac carries a recognizable social and emotional rhythm. ${entry.name} tends to reveal itself through behavior, pacing, and instinct before it ever becomes abstract interpretation.`,
          `That is what gives the symbol its staying power. It works because it points to a coherent style of responding to life rather than a random list of disconnected traits.`,
        ],
      },
      {
        title: `${entry.name} under stress`,
        body: [
          `When stretched too far, the same strengths can slide into ${entry.shadow}. This is where symbolic systems become most valuable: they give language to the difference between a gift and its distortion.`,
          `Recognizing that difference early makes the animal sign far more practical and far less superstitious.`,
        ],
      },
      {
        title: `${entry.name} in relationships and daily life`,
        body: [
          `Animal signs often become clearest in relationships. They show how a person trusts, protects, initiates, withdraws, or manages tension. That makes them useful not just for personality reading, but for understanding recurring dynamics.`,
          `In everyday life, ${entry.name} energy usually becomes most visible in habit. It appears in how choices are made, how stress is handled, and what kinds of environments feel naturally supportive.`,
        ],
      },
      {
        title: `The growth lesson of ${entry.name}`,
        body: [
          entry.lesson,
          `That lesson turns the sign into a path of development instead of a static personality tag. The symbol stays useful because it points toward practice.`,
        ],
      },
      {
        title: `A deeper way to read ${entry.name}`,
        body: [
          `The most rewarding use of the Chinese zodiac is not prediction. It is reflection. The sign reveals what kind of intelligence comes easily and what kind of wisdom still needs to be earned.`,
          `That is what makes the symbol durable. ${entry.name} remains helpful because the pattern keeps unfolding as life becomes more complex.`,
        ],
      },
    ],
    related: ['/chinese_zodiac', '/zodiac', '/numerology'],
  });
}

function createSimpleGuide(path, title, description, intro, sections, related = []) {
  return makeArticle({
    path,
    title,
    description,
    intro,
    sections,
    related,
  });
}

const pages = [];

pages.push(
  makeCollection({
    path: '/',
    title: 'Astrology, tarot, numerology, and Chinese zodiac guides',
    seoTitle: `Cosmic Secrets: Tarot, Zodiac, Numerology, and Chinese Zodiac Guides`,
    description:
      'Deep-dive guides on tarot, zodiac signs, numerology, and Chinese zodiac symbolism, plus a free profile tool that points you toward the topics most relevant to you.',
    eyebrow: 'Cosmic Secrets',
    heroSummary:
      'Explore the secrets of the cosmos',
    intro:
      'If you are here to understand a zodiac sign, look up a tarot card, compare numerology systems, or follow a symbolic thread more deeply, start with one of the guide hubs below. Each section is meant to help you move from broad meaning into more detailed interpretation.',
    sections: [
      {
        title: 'Start with the path that matches your question',
        body: [
          'Choose Zodiac if you want to understand personality patterns, compatibility, temperament, and growth. Choose Tarot if you are reading cards and want a deeper interpretation of symbolism, shadow, and practical meaning.',
          'Choose Numerology if you are exploring Life Path numbers or symbolic number systems. Choose Chinese Zodiac if you want to study the animal signs as patterns of instinct, behavior, and personal development.',
        ],
      },
      {
        title: 'Read broadly or go straight to your symbol',
        body: [
          'If you already know your sign, card, or number, use the guide hub to jump directly into the page you want. If you are still figuring that out, the profile tool below can point you toward a zodiac sign, a life path number, and a Chaldean number to begin with.',
          'Each guide is written to help you stay with the topic long enough to understand the pattern, not just skim a quick keyword and leave.',
        ],
      },
    ],
    featured: [
      { path: '/zodiac', title: 'Zodiac guides', summary: 'Twelve sign deep dives with compatibility, work style, and growth lessons.', icon: '♈', image: '/aries1.png', imageAlt: 'Aries zodiac artwork' },
      { path: '/tarot', title: 'Tarot guides', summary: 'Major Arcana, Minor Arcana, suit pages, and card-by-card interpretations.', icon: '✦', image: '/Fool.jpg', imageAlt: 'The Fool tarot card' },
      { path: '/numerology', title: 'Numerology guides', summary: 'Life Path, Chaldean, Pythagorean, and system overviews.', icon: '⑦', image: '/LifePath7.png', imageAlt: 'Life path numerology illustration' },
      { path: '/chinese_zodiac', title: 'Chinese zodiac guides', summary: 'Animal symbolism, shadow patterns, and practical interpretation.', icon: '龍', image: '/ChineseZodiacDragon.png', imageAlt: 'Chinese zodiac dragon artwork' },
    ],
    related: ['/zodiac', '/tarot', '/numerology', '/chinese_zodiac'],
  }),
  makeCollection({
    path: '/zodiac',
    title: 'Zodiac sign guides',
    seoTitle: `Zodiac Signs: Traits, Compatibility, Career, and Growth | ${SITE.name}`,
    description:
      'Browse all 12 zodiac signs with deeper interpretations of personality, compatibility, ambition, and the shadow lessons behind each sign.',
    eyebrow: 'Astrology • Zodiac',
    heroSummary:
      'The zodiac is less about stereotypes and more about patterns of energy: how people begin, bond, resist, protect, and grow.',
    intro:
      'Each zodiac sign is an archetypal style, not a flat personality category. The guides below explain what each sign is trying to learn, where its gifts become obvious, and how its shadow side tends to show up under stress.',
    cards: zodiacSigns.map((sign) => ({
      path: `/${sign.slug}`,
      title: sign.name,
      summary: `${sign.element} • ${sign.modality} • ${sign.essence}`,
      icon: sign.icon,
      image: zodiacImageMap[sign.slug],
      imageAlt: `${sign.name} zodiac artwork`,
    })),
    sections: [
      {
        title: 'How to read a zodiac sign well',
        body: [
          'A sign is best understood through its element, modality, ruler, and developmental lesson. That combination explains far more than a simple list of traits.',
          'The pages below are written to help you understand how each sign behaves when it is thriving, when it is stressed, and what kind of growth it tends to ask for over time.',
        ],
      },
    ],
    related: ['/', '/tarot', '/numerology'],
  }),
  makeCollection({
    path: '/tarot',
    title: 'Tarot guides and card meanings',
    seoTitle: `Tarot Meanings: Major Arcana, Minor Arcana, and Suit Guides | ${SITE.name}`,
    description:
      'Explore tarot with structured guides to the Major Arcana, Minor Arcana, suit meanings, and individual card interpretations.',
    eyebrow: 'Tarot',
    heroSummary:
      'Tarot works best when cards are read as relationships between symbol, context, and lived experience rather than as isolated fortune-cookie meanings.',
    intro:
      'Tarot becomes much easier to read once you understand the structure of the deck. Start with the Major Arcana if you want the big life lessons, then move into the Minor Arcana and suit pages if you want to understand how those lessons appear in ordinary life.',
    cards: [
      { path: '/major_arcana', title: 'Major Arcana', summary: 'The archetypal backbone of the deck and the larger lessons behind a reading.', icon: '◎', image: '/Fool.jpg', imageAlt: 'The Fool tarot card' },
      { path: '/minor_arcana', title: 'Minor Arcana', summary: 'The lived texture of daily experience through cups, wands, swords, and pentacles.', icon: '◇', image: `${CLOUDFRONT_BASE}/Wands01.jpg`, imageAlt: 'Ace of Wands tarot card' },
      { path: '/fools_journey', title: "The Fool's Journey", summary: 'A narrative way to understand the progression of the Major Arcana.', icon: '↺', image: '/Fool.jpg', imageAlt: 'The Fool tarot card' },
      { path: '/blog/how-to-read-tarot-for-yourself', title: 'How to read tarot for yourself', summary: 'A practical solo-reading workflow that stays grounded.', icon: '✎' },
    ],
    sections: [
      {
        title: 'Major Arcana and Minor Arcana',
        body: [
          'The Major Arcana speaks in life chapters, turning points, and defining lessons. The Minor Arcana explains how those lessons show up inside ordinary choices, relationships, conflicts, and responsibilities.',
          'Learning the difference keeps readings balanced. It helps you see when a reading is about a passing situation and when it is pointing to something much larger.',
        ],
      },
    ],
    related: ['/major_arcana', '/minor_arcana', '/blog/major-arcana-vs-minor-arcana'],
  }),
  makeCollection({
    path: '/numerology',
    title: 'Numerology guides',
    seoTitle: `Numerology Guides: Life Path, Chaldean, and Symbolic Systems | ${SITE.name}`,
    description:
      'Learn the basics of numerology, compare major systems, and explore life path and Chaldean number meanings with deeper context.',
    eyebrow: 'Numerology',
    heroSummary:
      'Numerology becomes more useful when numbers are treated as patterns of emphasis and growth rather than as rigid identity labels.',
    intro:
      'This section connects system overviews with number-specific pages so readers can move from general understanding to practical interpretation without getting lost in jargon.',
    cards: [
      { path: '/pythagorean_numerology', title: 'Pythagorean numerology', summary: 'The system most often used for Life Path readings.', icon: '△', image: '/LifePath1.png', imageAlt: 'Life path numerology illustration' },
      { path: '/chaldean_numerology', title: 'Chaldean numerology', summary: 'An older symbolic system focused on vibrational qualities.', icon: '☉', image: '/Sun1.png', imageAlt: 'Chaldean numerology illustration' },
      { path: '/kabbalistic_numerology', title: 'Kabbalistic numerology', summary: 'A spiritually oriented lens on language, soul work, and symbolism.', icon: '✶' },
      { path: '/chinese_numerology', title: 'Chinese numerology', summary: 'Cultural symbolism, sound association, and ideas of fortune.', icon: '八' },
    ],
    sections: [
      {
        title: 'Using numerology with more nuance',
        body: [
          'A single number can describe a recurring life theme without becoming a totalizing identity. That is why the strongest numerology pages explain both the gift and the shadow of a number.',
          'Readers can then use the number as a reflection tool instead of a shortcut.',
        ],
      },
    ],
    related: ['/blog/what-your-life-path-number-cant-do', '/chaldean_numerology', '/pythagorean_numerology'],
  }),
  makeCollection({
    path: '/major_arcana',
    title: 'Major Arcana card meanings',
    description:
      'Browse all 22 Major Arcana cards with deeper explanations of symbolism, life lessons, and practical interpretation.',
    eyebrow: 'Tarot • Major Arcana',
    heroSummary:
      'The Major Arcana tracks the defining lessons of the human journey, from first risk to integrated completion.',
    intro:
      'The Major Arcana cards are the archetypal spine of the tarot deck. They show up when a reading is less about a passing mood and more about the story your life is actively teaching you.',
    cards: majorArcana.map((card) => ({
      path: `/${card.slug}`,
      title: card.name,
      summary: `${card.numeral} • ${card.focus}`,
      icon: card.numeral,
      image: majorArcanaImageMap[card.slug],
      imageAlt: `${card.name} tarot card`,
    })),
    sections: [
      {
        title: 'Reading the Major Arcana',
        body: [
          'When several Major Arcana cards appear together, the reading often marks a threshold period. Choices matter, but so does timing, identity, and what must be outgrown.',
          "If you're new to the deck, the Fool's Journey can help you understand how the cards relate to one another instead of feeling like disconnected symbols.",
        ],
      },
    ],
    related: ['/fools_journey', '/tarot'],
  }),
  makeCollection({
    path: '/minor_arcana',
    title: 'Minor Arcana suit guides',
    description:
      'Explore the four suits of the Minor Arcana and how cups, wands, swords, and pentacles shape practical readings.',
    eyebrow: 'Tarot • Minor Arcana',
    heroSummary:
      'The Minor Arcana translates archetypal themes into real life: emotion, action, thought, work, conflict, and care.',
    intro:
      'Where the Major Arcana names the chapter, the Minor Arcana describes the scenes. The suit guides below help you read the practical terrain of a spread before you narrow in on individual cards.',
    cards: Object.values(suits).map((suit) => ({
      path: `/${suit.slug}`,
      title: suit.name,
      summary: `${suit.element} • ${suit.domain}`,
      icon: suit.icon,
      image: minorArcanaImage(suit.slug, 'ace'),
      imageAlt: `${suit.name} tarot card`,
    })),
    sections: [
      {
        title: 'How suits work in a spread',
        body: [
          'The suit shows the arena of experience. The rank shows the stage or style of that experience. Reading both together is what turns tarot from memorization into interpretation.',
          'That is why each suit on Cosmic Secrets includes a suit overview and a page for every card in the family.',
        ],
      },
    ],
    related: ['/tarot', '/major_arcana'],
  }),
  makeCollection({
    path: '/chinese_zodiac',
    title: 'Chinese zodiac animal guides',
    description:
      'Browse Chinese zodiac animal meanings with strengths, shadow traits, and practical symbolic interpretation.',
    eyebrow: 'Chinese Zodiac',
    heroSummary:
      'Chinese zodiac animals are symbolic temperaments that reveal instinctive style, recurring lessons, and the difference between a gift and its distortion.',
    intro:
      'These pages approach the Chinese zodiac with more structure and less caricature. Each animal is read as a symbolic pattern with strengths, shadow tendencies, and an implied growth lesson.',
    cards: chineseAnimals.map((animal) => ({
      path: `/${animal.slug}`,
      title: animal.name,
      summary: `${animal.essence} • ${animal.gift}`,
      icon: animal.name[0],
      image: chineseZodiacImageMap[animal.name],
      imageAlt: `${animal.name} Chinese zodiac artwork`,
    })),
    sections: [
      {
        title: 'How to use these animal guides',
        body: [
          'The animal guides are most useful when they prompt observation rather than superstition. Each one offers language for motivation, stress behavior, and the practical work of development.',
          'That approach keeps the symbolism grounded while preserving the imaginative richness that makes the Chinese zodiac compelling.',
        ],
      },
    ],
    related: ['/zodiac', '/numerology'],
  }),
);

pages.push(
  createSimpleGuide(
    '/fools_journey',
    "The Fool's Journey in tarot",
    'Understand the Major Arcana as a symbolic life journey from innocence and risk to integration and completion.',
    "The Fool's Journey is a useful way to read the Major Arcana as a sequence of developmental thresholds rather than twenty-two isolated cards.",
    [
      {
        title: 'Why the journey matters',
        body: [
          "The Fool's Journey gives the Major Arcana narrative logic. Instead of memorizing card definitions in a vacuum, readers begin to see how one lesson leads into the next.",
          'That makes the deck easier to remember and far more meaningful in practice. The cards become stages of becoming, not just symbols on a table.',
        ],
      },
      {
        title: 'From innocence to integration',
        body: [
          'The journey begins with openness and risk, moves through relationship, discipline, rupture, surrender, and renewal, and concludes in a fuller form of completion.',
          'Because the journey is cyclical, not linear, readers often revisit the same lessons at deeper levels over time.',
        ],
      },
    ],
    ['/major_arcana', '/tarot'],
  ),
  createSimpleGuide(
    '/pythagorean_numerology',
    'Pythagorean numerology',
    'A practical overview of Pythagorean numerology, the logic behind Life Path numbers, and how to use the system with more nuance.',
    'Pythagorean numerology is the system most readers encounter first because it is the framework usually used for Life Path calculations. Its popularity makes it accessible, but accessibility can also flatten the method into generic personality blurbs.',
    [
      {
        title: 'What the system emphasizes',
        body: [
          'The Pythagorean system treats numbers as symbolic patterns that illuminate personality, motivation, and recurring lessons. It is especially known for Life Path, Expression, and Soul Urge interpretations.',
          'Its strength lies in structure. The system is easy to teach, easy to calculate, and flexible enough to support both beginner and advanced readings.',
        ],
      },
      {
        title: 'How to use it well',
        body: [
          'The best use of Pythagorean numerology is not to force identity into one number, but to notice how several patterns reinforce or challenge one another.',
          'That is also why Life Path pages on this site focus on growth, shadow, and practical application instead of pure flattery.',
        ],
      },
    ],
    ['/numerology'],
  ),
  createSimpleGuide(
    '/chaldean_numerology',
    'Chaldean numerology',
    'An overview of Chaldean numerology, its symbolic logic, and the interpretive style that makes it different from modern simplified systems.',
    'Chaldean numerology is often described as older, more mystical, and more symbolically dense than Pythagorean numerology. Those labels are only helpful if they are translated into actual reading practice.',
    [
      {
        title: 'What makes Chaldean numerology distinct',
        body: [
          'Chaldean numerology places strong emphasis on vibrational quality and symbolic correspondence. Rather than feeling purely mathematical, it often reads like a language of energetic signatures.',
          'That symbolic emphasis is why many readers use Chaldean numbers to explore tone, temperament, and the way a name carries meaning.',
        ],
      },
      {
        title: 'Why the number pages matter',
        body: [
          'Because the system can feel abstract, number-specific articles are especially helpful. They give readers a grounded way to recognize how a number behaves when expressed well and when distorted by stress.',
          'Used well, the system becomes less mysterious and more actionable.',
        ],
      },
    ],
    ['/numerology'],
  ),
  createSimpleGuide(
    '/kabbalistic_numerology',
    'Kabbalistic numerology',
    'A symbolic introduction to Kabbalistic numerology and how it differs from more personality-driven systems.',
    'Kabbalistic numerology is usually approached as a contemplative system. It is less concerned with quick personality typing and more interested in the relationship between language, soul development, and symbolic meaning.',
    [
      {
        title: 'How this system is different',
        body: [
          'Where some numerology systems foreground outward temperament, Kabbalistic approaches often feel more inward and symbolic. The point is to reveal deeper patterns of consciousness, value, and spiritual orientation.',
          'That makes it especially compelling for readers who already treat mystical systems as reflective tools rather than fixed destiny maps.',
        ],
      },
      {
        title: 'A grounded way to use it',
        body: [
          'Because the system can become abstract quickly, it helps to pair symbolic insight with reflective questions. What themes repeat? What values are being refined? What kind of growth seems to ask for humility rather than control?',
          'That framing keeps the material spacious without making it vague.',
        ],
      },
    ],
    ['/numerology'],
  ),
  createSimpleGuide(
    '/chinese_numerology',
    'Chinese numerology',
    'Learn how Chinese numerology uses sound, symbolism, and cultural meaning rather than a single universal personality system.',
    'Chinese numerology is often misunderstood by readers expecting a one-size-fits-all personality map. In practice, it is deeply shaped by language, cultural association, and ideas of fortune tied to sound and symbolism.',
    [
      {
        title: 'What Chinese numerology emphasizes',
        body: [
          'Numbers may be considered fortunate or unfortunate based on pronunciation, cultural context, and long-standing symbolic association. This makes the system less psychological and more situational than many Western numerology models.',
          'That does not make it superficial. It simply means the interpretive logic is different.',
        ],
      },
      {
        title: 'Reading it with respect',
        body: [
          'The strongest approach is to understand the system on its own terms rather than forcing it into a framework built for Life Path readings.',
          'Used carefully, Chinese numerology expands your sense of what a symbolic number system can actually be.',
        ],
      },
    ],
    ['/numerology', '/chinese_zodiac'],
  ),
  createSimpleGuide(
    '/about',
    'About Cosmic Secrets',
    'Learn what Cosmic Secrets covers, how the site approaches symbolism, and what the editorial focus of the project is.',
    'Cosmic Secrets is an editorial project about tarot, zodiac, Chinese zodiac, and numerology. The goal is not to claim scientific authority. It is to publish better-organized symbolic guides that are genuinely readable and internally connected.',
    [
      {
        title: 'Editorial approach',
        body: [
          'This site treats mystical systems as languages of meaning, reflection, and archetype. That means the writing aims to be clear, honest, and useful without pretending certainty where none exists.',
          'The focus stays on interpretation, symbolism, and lived experience so readers can move from curiosity into a more grounded understanding of what they are studying.',
        ],
      },
      {
        title: 'What you will find here',
        body: [
          'The guides are designed to be longer, calmer, and more useful than the usual one-paragraph mystical summaries. Wherever possible, a topic is broken into strengths, shadow patterns, relationship dynamics, practical meaning, and the larger growth lesson behind the symbol.',
          'That structure helps the pages work as references. You can dip in for a quick answer, or stay and read the subject more deeply without feeling like the explanation runs out after a few lines.',
        ],
      },
    ],
    ['/', '/contact', '/faq'],
  ),
  createSimpleGuide(
    '/contact',
    'Contact Cosmic Secrets',
    'How to get in touch with Cosmic Secrets for editorial questions, partnerships, or corrections.',
    'If you need to reach Cosmic Secrets about editorial corrections, partnership inquiries, or general questions about the site, this page explains the best way to do that.',
    [
      {
        title: 'Editorial questions and corrections',
        body: [
          `For corrections or content questions, email ${SITE.email}. Include the page URL and a short description of what you think should be reviewed.`,
          'Clear correction workflows help symbolic sites build trust over time, especially when content is long-form and evergreen.',
        ],
      },
      {
        title: 'Partnerships and advertising',
        body: [
          'Cosmic Secrets is built as an editorial destination, so potential partnerships should fit the site’s topic areas and reader expectations.',
          'Relevant outreach includes advertising, sponsorship, and collaboration ideas that are aligned with astrology, tarot, self-reflection, journaling, and adjacent lifestyle topics.',
        ],
      },
    ],
    ['/about', '/privacy_policy'],
  ),
  createSimpleGuide(
    '/privacy_policy',
    'Privacy policy',
    'Read the Cosmic Secrets privacy policy, including how the site handles optional analytics cookies and basic visitor data.',
    'This privacy policy explains what Cosmic Secrets collects, what remains optional, and how cookie consent affects analytics on the site.',
    [
      {
        title: 'What data the site uses',
        body: [
          'Cosmic Secrets can use basic analytics tools to understand page performance, but only when a visitor accepts optional cookies. The site content itself remains accessible without that consent.',
          'The profile calculator runs in the browser and does not require account creation or a server-side personal data submission flow.',
        ],
      },
      {
        title: 'Consent choices',
        body: [
          'Visitors can accept or decline optional cookies through the consent banner. Declining prevents optional analytics from initializing in the browser.',
          'This keeps consent meaningful instead of decorative, which is the standard the site is aiming to meet.',
        ],
      },
    ],
    ['/contact', '/faq'],
  ),
  createSimpleGuide(
    '/faq',
    'Cosmic Secrets FAQ',
    'Answers to common questions about the site’s symbolic approach, cookie choices, and how the guides are meant to be used.',
    'These are the most common questions readers have about how Cosmic Secrets works and how to use the guides responsibly.',
    editorialFaqs.map((faq) => ({
      title: faq.question,
      body: [faq.answer],
    })),
    ['/about', '/privacy_policy'],
  ),
);

zodiacSigns.forEach((sign) => pages.push(createZodiacPage(sign)));
majorArcana.forEach((card) => pages.push(createMajorArcanaPage(card)));
Object.values(suits).forEach((suit) => {
  pages.push(createSuitHubPage(suit));
  ranks.forEach((rank) => pages.push(createMinorArcanaPage(suit, rank)));
});
lifePathNumbers.forEach((entry) => pages.push(createLifePathPage(entry)));
chaldeanNumbers.forEach((entry) => pages.push(createChaldeanPage(entry)));
chineseAnimals.forEach((entry) => pages.push(createChineseZodiacPage(entry)));
blogPosts.forEach((post) =>
  pages.push(
    makeArticle({
      path: post.path,
      title: post.title,
      description: post.description,
      intro: post.intro,
      sections: post.sections,
      related: ['/blog', '/tarot', '/numerology'],
    }),
  ),
);

pages.push(
  makeCollection({
    path: '/blog',
    title: 'Cosmic Secrets blog',
    description:
      'Long-form editorial posts about tarot reading, numerology interpretation, and symbolic frameworks that deserve more nuance.',
    eyebrow: 'Editorial',
    heroSummary:
      'These essays go deeper into reading method, interpretation, and the kinds of questions that make symbolic systems more useful.',
    intro:
      'Use the blog when you want more than a definition page. These pieces are meant to help you read with more clarity, ask better questions, and understand how the systems work in practice.',
    cards: blogPosts.map((post, index) => ({
      path: post.path,
      title: post.title,
      summary: post.description,
      icon: `${index + 1}`,
    })),
    sections: [
      {
        title: 'What the blog covers',
        body: [
          'The blog covers broader interpretive questions: how to read tarot responsibly, how to avoid flattening numerology into a single label, and how to understand the larger architecture of symbolic systems.',
          'The goal is to give you more context when a short meaning page is not enough on its own.',
        ],
      },
    ],
    related: ['/tarot', '/numerology'],
  }),
);

const aliasMap = [
  ['/info', '/'],
  ['/blog/1', '/blog/how-to-read-tarot-for-yourself'],
  ['/blog/2', '/blog/major-arcana-vs-minor-arcana'],
  ['/blog/luna', '/blog/how-to-read-tarot-for-yourself'],
  ['/blog/star', '/blog/major-arcana-vs-minor-arcana'],
  ['/blog/maven', '/blog/what-your-life-path-number-cant-do'],
  ['/blog/zara', '/blog/what-your-life-path-number-cant-do'],
];

aliasMap.forEach(([aliasPath, canonicalPath]) => {
  const canonicalPage = pages.find((page) => page.path === canonicalPath);
  if (!canonicalPage) {
    return;
  }

  pages.push(
    createPage({
      ...canonicalPage,
      path: aliasPath,
      canonicalPath,
      aliasFor: canonicalPath,
      noindex: true,
    }),
  );
});

export const allPages = pages;
export const pagesByPath = Object.fromEntries(pages.map((page) => [page.path, page]));
export const renderablePages = pages.filter((page) => !page.aliasFor);
export const profileTargets = {
  zodiac: Object.fromEntries(zodiacSigns.map((sign) => [sign.name, `/${sign.slug}`])),
  lifePath: Object.fromEntries(lifePathNumbers.map((entry) => [String(entry.number), `/${entry.slug}`])),
  chaldean: Object.fromEntries(chaldeanNumbers.map((entry) => [String(entry.number), `/${entry.slug}`])),
};
export const pageGroups = {
  zodiacSigns,
  majorArcana,
  lifePathNumbers,
  chaldeanNumbers,
  chineseAnimals,
  suits: Object.values(suits),
  ranks,
  blogPosts,
};
